import React, { useState, useEffect } from 'react';
import { FaWhatsapp } from "react-icons/fa";
import { FaCheckCircle, FaTimesCircle, FaSpinner } from "react-icons/fa";
import { useLocation, useNavigate } from 'react-router-dom';
import whatsappService from '../../services/whatsappService';
import { useMetaSdk } from '../../hooks/useMetaSdk';
import { useUser } from '../../context/UserContext';
import { env } from '../../utils/env';
import { PrimaryButton, CloseButton } from '../../components/Buttons';

const WhatsAppSetup = () => {
    const { fbReady, error: fbError } = useMetaSdk();
    const { selectedProject } = useLocation().state;
  
    const { userInfo, setUserInfo } = useUser();
    const navigate = useNavigate();
    const [isConnected, setIsConnected] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [pin, setPin] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [currentStep, setCurrentStep] = useState(1);
    const [fbStatus, setFbStatus] = useState(null);

    const [waData, setWaData] = useState({
        waba_id: '',
        phone_number_id: '',
        business_id: '',
        exchangeable_code: '',
        // Coexistence (WA Business app stays on the same number — no PIN/registration).
        is_coexistence: false,
        phone_number: '',
    });

    // Helper function to validate code
    const validateCode = (code) => {
        // Check 1: Ensure code is not empty/invalid
        if (!code || typeof code !== 'string' || code.trim() === '') {
            console.error('Invalid or empty authentication code received');
            setError("Invalid or missing authentication code");
            return false;
        }

        console.log('Facebook authentication code received:', code);

        return true;
    };

    // Helper function to validate redirect URI
    const validateRedirectUri = (authResponse) => {
        const expectedRedirectUri = env("REACT_APP_WA_REDIRECT_URI");
        if (expectedRedirectUri && authResponse.redirectUri) {
            console.log('Expected redirect URI:', expectedRedirectUri);
            console.log('Actual redirect URI:', authResponse.redirectUri);
            
            if (authResponse.redirectUri !== expectedRedirectUri) {
                console.error('Redirect URI mismatch');
                setError("Redirect URI mismatch. Please try again.");
                return false;
            }
        }
        return true;
    };

    const launchSignup = async () => {
        if (!window.FB) {
            setError("Facebook SDK not loaded. Please refresh the page.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            window.FB.getLoginStatus(function (response) {
                console.log("FB SDK Status:", response);
                setFbStatus(response);

                if (response.status === "connected" && response.authResponse?.code) {
                    // Validate the existing code
                    if (!validateCode(response.authResponse.code)) {
                        setLoading(false);
                        return;
                    }

                    // Validate redirect URI if available
                    if (!validateRedirectUri(response.authResponse)) {
                        setLoading(false);
                        return;
                    }

                    // Already connected and has a valid code
                    setIsConnected(true);
                    setCurrentStep(4);
                    setSuccess("Already connected to WhatsApp Business API");
                    setWaData((d) => ({
                        ...d,
                        exchangeable_code: response.authResponse.code,
                    }));
                    setLoading(false);
                    return;
                }

                // Not connected or no valid code, proceed with login
                window.FB.login(
                    function (loginResponse) {
                        console.log("Facebook login response:", loginResponse);
                        
                        if (loginResponse?.authResponse?.code) {
                            // Validate the new code
                            if (!validateCode(loginResponse.authResponse.code)) {
                                setLoading(false);
                                return;
                            }

                            // Validate redirect URI
                            if (!validateRedirectUri(loginResponse.authResponse)) {
                                setLoading(false);
                                return;
                            }

                            // Stash the code; if Meta has already fired WA_EMBEDDED_SIGNUP
                            // marking this as a coexistence flow, skip the PIN modal and
                            // submit straight away — no Cloud-API registration / PIN required.
                            setWaData((d) => {
                                const next = { ...d, exchangeable_code: loginResponse.authResponse.code };
                                if (next.is_coexistence) {
                                    // Defer submit so React commits the new state first.
                                    setTimeout(() => handleSubmit(next), 0);
                                    setCurrentStep(4);
                                } else {
                                    setShowModal(true);
                                    setCurrentStep(3);
                                }
                                return next;
                            });
                        } else {
                            console.error('No authentication code received from Facebook');
                            setError("User cancelled login or no authentication code received");
                            setCurrentStep(1);
                        }
                        setLoading(false);
                    },
                    {
                        config_id: env("REACT_APP_WA_CONFIG_ID"),
                        response_type: "code",
                        override_default_response_type: true,
                        scope: "whatsapp_business_management,business_management,whatsapp_business_messaging",
                        extras: {
                            setup: {},
                            featureType: "whatsapp_business_app_onboarding",
                            sessionInfoVersion: "3",
                        },
                    }
                );
            });
        } catch (err) {
            console.error("Facebook login error:", err);
            setError("Failed to initialize Facebook login. Please try again.");
            setLoading(false);
            setCurrentStep(1);
        }
    };

    // 3. Listening for Data from Meta
    //
    // Meta's Embedded Signup (including the **coexistence flow** —
    // featureType: whatsapp_business_app_onboarding + sessionInfoVersion: 3)
    // emits several event types via window.postMessage. We handle each so
    // both classic Cloud-API onboarding AND coexistence-with-WhatsApp-
    // Business-app onboarding land cleanly.
    //
    //   FINISH                                — classic ES completed (phone + WABA)
    //   FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING — coexistence ES completed; the
    //                                            user kept the WA Business app
    //                                            on the same number and we now
    //                                            have a phone_number_id pointing
    //                                            at the existing number
    //   FINISH_ONLY_WABA                       — only the WABA was created (no
    //                                            phone yet); user must add a
    //                                            number later
    //   CANCEL                                 — user dismissed the dialog
    //   ERROR                                  — flow errored upstream
    useEffect(() => {
        const listener = (event) => {
            // Only process messages from Facebook domains
            if (!event.origin.endsWith("facebook.com")) return;

            // Skip non-JSON messages (the SDK also posts other things)
            if (typeof event.data !== "string" || !event.data.trim().startsWith("{")) return;

            let data;
            try {
                data = JSON.parse(event.data);
            } catch {
                return; // not our message
            }

            if (data.type !== "WA_EMBEDDED_SIGNUP") return;
            console.log("[ES] message:", data);

            const evt = data.event || "";
            const payload = data.data || {};

            if (evt.startsWith("FINISH")) {
                const isCoexistence = evt === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING";
                const newWaData = {
                    ...waData,
                    waba_id: payload.waba_id,
                    phone_number_id: payload.phone_number_id,
                    business_id: payload.business_id,
                    is_coexistence: isCoexistence,
                    // Some Meta payloads include the actual phone string; the BE will accept
                    // either phone_number_id or phone_number, so we forward whatever is present.
                    phone_number: payload.phone_number || payload.display_phone_number || "",
                };
                setWaData(newWaData);
                if (isCoexistence) {
                    console.log("[ES] Coexistence onboarding finished — preserving WhatsApp Business app on the same number.");
                } else if (evt === "FINISH_ONLY_WABA") {
                    setError("WABA created but no phone number was added. Add a phone number in Meta Business Suite, then re-run setup.");
                }
            } else if (evt === "CANCEL") {
                setError("Signup cancelled.");
                setCurrentStep(1);
            } else if (evt === "ERROR") {
                setError(payload?.error_message || "Embedded Signup failed. Please try again.");
                setCurrentStep(1);
            }
        };

        window.addEventListener("message", listener);
        return () => window.removeEventListener("message", listener);
    }, [waData]);

    // 4 & 5. Submit to BE. Branches on coexistence:
    //   - Normal flow: requires phoneNumberId + 6-digit PIN.
    //   - Coexistence flow: no PIN; BE resolves the phone entry from phoneNumberId
    //     (or phoneNumber as a fallback) inside the WABA's phone list.
    const handleSubmit = async (data) => {
        const src = data || waData;
        if (!src.is_coexistence) {
            if (!pin || pin.length !== 6) {
                setError("Please enter a valid 6-digit PIN");
                return;
            }
            if (!src.phone_number_id) {
                setError("Missing phone number id from Meta. Please re-run setup.");
                return;
            }
        } else if (!src.phone_number_id && !src.phone_number) {
            setError("Missing phone information from Meta. Please re-run setup.");
            return;
        }

        setLoading(true);
        setError("");
        try {
            const payload = {
                projectId: selectedProject.projectId,
                wabaId: src.waba_id,
                exchangeableCode: src.exchangeable_code,
                phoneNumberId: src.phone_number_id || undefined,
                isCoexistence: !!src.is_coexistence,
                ...(src.is_coexistence
                    ? { phoneNumber: src.phone_number || undefined }
                    : { pin }),
            };
            console.log("[ES] submit payload:", payload);

            await whatsappService.submitEmbeddedSignup(payload);

            setIsConnected(true);
            setSuccess(
                src.is_coexistence
                    ? "WhatsApp connected (coexistence — your WA Business app continues to work)."
                    : "WhatsApp connected successfully!"
            );
            setShowModal(false);
        } catch (err) {
            console.error("WhatsApp setup failed:", err);
            setError(err?.detail || err?.message || "Signup failed");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitPin = () => handleSubmit();

    const handlePinChange = (e) => {
        const value = e.target.value;
        if (value.length <= 6) {
            setPin(value);
        }
    };

    const steps = [
        {
            title: "Initialize Facebook SDK",
            description: "Setting up the Facebook SDK for WhatsApp integration",
            status: fbReady ? "completed" : "current"
        },
        {
            title: "Connect Facebook Account",
            description: "Log in to your Facebook account to proceed",
            status: currentStep > 1 ? "completed" : "pending"
        },
        {
            title: "Set up a PIN",
            description: "Enter a 6 digit verification PIN ",
            status: currentStep > 2 ? "completed" : "pending"
        },
        {
            title: "Setup Complete",
            description: "Your WhatsApp Business API is now connected",
            status: isConnected ? "completed" : "pending"
        }
    ];

    const getStepIcon = (status) => {
        switch (status) {
            case "completed":
                return <FaCheckCircle className="w-6 h-6 text-green-500" />;
            case "current":
                return <FaSpinner className="w-6 h-6 text-blue-500 animate-spin" />;
            case "pending":
                return <FaTimesCircle className="w-6 h-6 text-gray-400" />;
            default:
                return null;
        }
    };

    return (
        <div className="max-w-3xl mx-auto p-8 bg-white rounded-xl shadow-lg">
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/profile#onboarding')}
                        className="text-gray-600 hover:text-gray-800 font-medium text-sm flex items-center gap-2"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        
                    </button>
                </div>
                <div className="flex items-center gap-4">
                    <FaWhatsapp className="w-8 h-8 text-[#25D366]" />
                    <h2 className="text-2xl font-semibold text-gray-800">WhatsApp Setup</h2>
                </div>
                {!isConnected && (
                    <button
                        className={`px-6 py-2 rounded-lg font-medium text-white transition-colors
                            ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-[#128C7E]'}`}
                        onClick={launchSignup}
                        disabled={loading || !fbReady}
                    >
                        {loading ? 'Connecting...' : (isConnected ? 'Update Credentials' : 'Connect')}
                    </button>
                )}
            </div>

            {/* Steps */}
            <div className="space-y-4">
                {steps.map((step, index) => (
                    <div key={index} className={`p-6 rounded-lg border ${
                        step.status === 'completed' ? 'bg-green-50 border-green-200' :
                        step.status === 'current' ? 'bg-blue-50 border-blue-200' :
                        'bg-gray-50 border-gray-200'
                    }`}>
                        <div className="flex items-start gap-4">
                            {getStepIcon(step.status)}
                            <div>
                                <h3 className="text-lg font-medium text-gray-800">{step.title}</h3>
                                <p className="text-sm text-gray-600">{step.description}</p>
                            </div>
                        </div>
                        {step.status === "current" && fbStatus && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="bg-white p-4 rounded-lg">
                                    <p className="text-sm text-gray-700">
                                        Facebook Status: <span className="font-medium">{fbStatus.status}</span>
                                    </p>
                                    {fbStatus.authResponse && (
                                        <p className="text-sm text-gray-700">
                                            User ID: <span className="font-medium">{fbStatus.authResponse.userID}</span>
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Status Messages */}
            {isConnected && (
                <div className="mt-6 p-4 flex items-center gap-4">
                    {/* <FaCheckCircle className="w-6 h-6 text-green-500" /> */}
                    <div>
                        <h3 className="text-lg font-medium text-green-800">Connected Successfully!</h3>
                        <p className="text-sm text-green-600">Your WhatsApp Business API is now connected and ready to use.</p>
                    </div>
                </div>
            )}

            {fbError && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {fbError}
                </div>
            )}
            {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                </div>
            )}
            {success && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                    {success}
                </div>
            )}

            {/* PIN Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl w-full max-w-md mx-4">
                        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                            <div className="flex items-center gap-4">
                                <button
                                    className="text-gray-600 hover:text-gray-800 font-medium text-sm flex items-center gap-2"
                                    onClick={() => setShowModal(false)}
                                    disabled={loading}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7 7-7" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12h18" />
                                    </svg>
                                </button>
                                <h3 className="text-xl font-semibold text-gray-800">Enter a 6-digit PIN</h3>
                            </div>
                            <CloseButton
                                className="text-gray-400 hover:text-gray-600 text-2xl"
                                onClick={() => setShowModal(false)}
                                disabled={loading}
                            >
                                ×
                            </CloseButton>
                        </div>
                        <div className="p-6">
                            <p className="text-gray-600 mb-6">
                            Please set a verification PIN below to verify the connection.
                            </p>
                            <div className="mb-6">
                                <input
                                    type="text"
                                    maxLength={6}
                                    value={pin}
                                    onChange={handlePinChange}
                                    placeholder="Enter PIN"
                                    className="w-full px-4 py-3 text-xl text-center tracking-widest border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                                    disabled={loading}
                                />
                            </div>
                        </div>
                        <div className="p-6 border-t border-gray-200 flex justify-between">
                            {/* <button
                                className="px-6 py-3 rounded-lg font-semibold text-gray-600 border border-gray-300 hover:bg-gray-50 transition-colors"
                                onClick={() => setShowModal(false)}
                                disabled={loading}
                            >
                                Go Back
                            </button> */}
                            <PrimaryButton
                                className={`px-6 py-3 rounded-lg font-medium text-white transition-colors
                                    ${(!pin || pin.length !== 6 || loading) 
                                        ? 'bg-gray-400 cursor-not-allowed' 
                                        : 'bg-flamingo-500 hover:bg-flamingo-600'}`}
                                onClick={handleSubmitPin}
                                disabled={!pin || pin.length !== 6 || loading}
                            >
                                {loading ? 'Verifying...' : 'Verify & Connect'}
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WhatsAppSetup;